<template>
    <v-container class="pa-2">
        <!-- Header sticky -->
        <v-card class="rounded-xl elevation-2 px-3 py-2 mb-3 sticky-header">
            <!-- Riga titolo -->
            <div class="d-flex align-center">
                <v-btn icon small text color="primary" @click="$emit('close')">
                    <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <div class="ml-1 flex-grow-1 text-subtitle-2 font-weight-bold primary--text">
                    Seleziona oratore
                </div>
            </div>
            <!-- Search -->
            <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-account-search-outline"
                placeholder="Cerca per nome..."
                outlined rounded dense hide-details clearable
                class="mt-1"
            />
            <!-- Chips filtri + switch -->
            <div class="d-flex flex-wrap align-center mt-1" style="gap:6px; min-height:24px">
                <v-chip v-if="congregazioneFiltro" x-small color="teal" dark>
                    <v-icon x-small left>mdi-home-city-outline</v-icon>
                    {{ congregazioneFiltro.nome }}
                </v-chip>
                <v-chip v-if="schemaFiltroNumero" x-small color="primary" dark>
                    <v-icon x-small left>mdi-book-open-outline</v-icon>
                    N.{{ schemaFiltroNumero }}
                </v-chip>
                <span v-if="!congregazioneFiltro" class="text-caption grey--text">{{ filtrati.length }} oratori</span>
                <v-spacer />
                <v-btn-toggle :value="filtroOratori" dense mandatory
                              @change="$emit('update:filtroOratori', $event)">
                    <v-btn x-small value="">Tutti</v-btn>
                    <v-btn x-small value="disponibili">Disponibili</v-btn>
                    <v-btn x-small value="riserva">Riserva</v-btn>
                </v-btn-toggle>
            </div>
            <!-- Conteggio sotto, solo se c'è filtro congregazione -->
            <div v-if="congregazioneFiltro" class="text-caption grey--text mt-1">
                {{ filtrati.length }} oratori
            </div>
        </v-card>

        <!-- Skeleton -->
        <template v-if="loading">
            <v-card v-for="n in 5" :key="n" class="rounded-xl elevation-1 mb-2 px-3 py-2">
                <v-skeleton-loader type="list-item-avatar-two-line" />
            </v-card>
        </template>

        <!-- Lista -->
        <template v-else>
            <v-card
                v-for="item in filtrati"
                :key="item.oratore.id"
                class="rounded-xl elevation-1 mb-2 ora-card"
                :class="item.indisponibileSecco ? 'ora-indisp' : ''"
                @click="onCardClick(item)"
            >
                <div class="px-3 py-2">
                    <div class="d-flex align-center mb-1">
                        <v-avatar size="36" :color="item.indisponibile ? 'grey lighten-2' : 'primary'" class="mr-3 flex-shrink-0">
                            <span :class="item.indisponibile ? 'grey--text' : 'white--text'"
                                  style="font-size:0.6rem;font-weight:700">
                                {{ initiali(item.oratore.nominativo) }}
                            </span>
                        </v-avatar>
                        <div class="flex-grow-1" style="min-width:0">
                            <div class="d-flex align-center flex-wrap" style="gap:4px">
                                <span class="text-body-2 font-weight-bold"
                                      :class="item.indisponibile ? 'grey--text' : ''">
                                    {{ item.oratore.nominativo }}
                                </span>
                                <v-chip v-if="item.oratore.isAnziano"      x-small color="teal" dark>A</v-chip>
                                <v-chip v-if="item.oratore.isSM"           x-small color="blue darken-1" dark>SM</v-chip>
                                <v-chip v-if="item.oratore.isAssistente"   x-small color="grey darken-2" dark>ASS</v-chip>
                                <v-chip v-if="item.oratore.isCoordinatore" x-small color="deep-orange darken-4" dark>ADM</v-chip>
                                <v-chip v-if="item.oratore.isAdmin"        x-small color="orange darken-1" dark>APP-ADM</v-chip>
                                <chip-login-oratore :oratore="item.oratore" />
                            </div>
                            <div class="text-caption grey--text">{{ item.oratore.congregazione && item.oratore.congregazione.nome }}</div>
                        </div>
                        <v-icon v-if="!item.indisponibileSecco" color="grey lighten-1" size="18">mdi-chevron-right</v-icon>
                    </div>

                    <!-- Due colonne ultima uscita -->
                    <div class="d-flex" style="gap:6px">
                        <div class="ora-col">
                            <div class="ora-label">Ultima uscita</div>
                            <div class="ora-valore" :class="!dataValida(item.dataUltimoDiscorso) ? 'ora-mai' : ''">
                                {{ fmtData(item.dataUltimoDiscorso) }}
                            </div>
                            <div v-if="item.numeroUltimoDiscorso && item.numeroUltimoDiscorso.trim() !== '-'" class="ora-schema">N.{{ item.numeroUltimoDiscorso }}</div>
                            <div v-if="item.congregazioneEsterna && item.congregazioneEsterna !== '-'" class="ora-cong">
                                {{ item.congregazioneEsterna }}
                            </div>
                        </div>
                        <div class="ora-divider"></div>
                        <div class="ora-col">
                            <div class="ora-label">Uscita a {{ nomeCongregazioneArrivo }}</div>
                            <div class="ora-valore" :class="!dataValida(item.dataUltimoDiscorsoInterno) ? 'ora-mai' : ''">
                                {{ fmtData(item.dataUltimoDiscorsoInterno) }}
                            </div>
                        </div>
                    </div>

                    <!-- Motivazione indisponibilità -->
                    <div v-if="item.motivazione" class="ora-motivazione mt-1">
                        <v-icon x-small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
                        {{ item.motivazione }}
                    </div>
                </div>
            </v-card>

            <div v-if="filtrati.length === 0" class="text-center grey--text py-10">
                <v-icon large color="grey lighten-1">mdi-account-off-outline</v-icon>
                <div class="mt-2 text-body-2">Nessun oratore trovato</div>
            </div>
        </template>

        <!-- Dialog avviso indisponibilità soft -->
        <v-dialog v-model="dialogAvviso" max-width="340">
            <v-card class="rounded-xl">
                <v-card-title class="text-subtitle-1 font-weight-bold pb-1">
                    <v-icon color="warning" class="mr-2">mdi-alert-outline</v-icon>
                    Attenzione
                </v-card-title>
                <v-card-text v-if="pendingItem" class="pb-2">
                    <div v-if="pendingItem.motivazione" class="text-body-2 mb-2">{{ pendingItem.motivazione }}</div>
                    <div class="text-body-2">Vuoi selezionare comunque <strong>{{ pendingItem.oratore.nominativo }}</strong>?</div>
                </v-card-text>
                <v-card-actions class="pt-0 px-4 pb-3">
                    <v-btn text rounded @click="dialogAvviso = false">Annulla</v-btn>
                    <v-spacer />
                    <v-btn color="primary" rounded depressed @click="confermaSelezione">Seleziona</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
import { VSkeletonLoader } from 'vuetify/lib';
import ChipLoginOratore from '@/components/ChipLoginOratore.vue';
export default {
    name: 'SelectOratore',
    components: { VSkeletonLoader, ChipLoginOratore },
    props: {
        items:                   { type: Array,           default: () => [] },
        loading:                 { type: Boolean,         default: false },
        filtroOratori:           { type: String,          default: 'disponibili' }, // '' | 'disponibili' | 'riserva'
        nomeCongregazioneArrivo: { type: String,          default: 'questa congregazione' },
        congregazioneFiltro:     { type: Object,          default: null },
        schemaFiltroNumero:      { type: [String, Number], default: null },
    },
    data: () => ({ search: '', dialogAvviso: false, pendingItem: null }),
    computed: {
        filtrati() {
            let lista = this.items;
            if (this.search) {
                const s = this.search.toLowerCase();
                lista = lista.filter(od => (od.oratore?.nominativo || '').toLowerCase().includes(s));
            }
            return lista;
        },
    },
    methods: {
        dataValida(d) {
            return !!d;
        },
        fmtData(d) {
            if (!d) return '—';
            return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
        },
        onCardClick(item) {
            if (item.indisponibileSecco) return;           // bloccato sempre
            if (item.indisponibile) {                      // avviso, poi seleziona
                this.pendingItem  = item;
                this.dialogAvviso = true;
                return;
            }
            this.$emit('selected', item);
        },
        confermaSelezione() {
            this.dialogAvviso = false;
            if (this.pendingItem) this.$emit('selected', this.pendingItem);
            this.pendingItem = null;
        },
        initiali(nome) {
            if (!nome) return '?';
            const p = nome.trim().split(/\s+/);
            return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : nome[0].toUpperCase();
        },
    },
};
</script>

<style scoped>
.sticky-header { position: sticky; top: 0; z-index: 4; }
.ora-card { cursor: pointer; transition: box-shadow 0.15s; }
.ora-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.13) !important; }
.ora-indisp { opacity: 0.55; cursor: default; }
.ora-col { flex: 1; min-width: 0; }
.ora-label { font-size: 0.68rem; color: rgba(0,0,0,0.45); font-weight: 600; margin-bottom: 2px; }
.theme--dark .ora-label { color: rgba(255,255,255,0.4); }
.ora-valore { font-size: 0.78rem; font-weight: 700; }
.ora-mai { color: #9e9e9e; font-weight: 400; }
.ora-schema { font-size: 0.68rem; color: #1976d2; }
.theme--dark .ora-schema { color: #90caf9; }
.ora-cong { font-size: 0.68rem; color: rgba(0,0,0,0.5); }
.theme--dark .ora-cong { color: rgba(255,255,255,0.4); }
.ora-divider { width: 1px; background: rgba(0,0,0,0.08); margin: 0 6px; }
.theme--dark .ora-divider { background: rgba(255,255,255,0.1); }
.ora-motivazione { font-size: 0.72rem; display: flex; align-items: center; }
</style>
